import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ForumIcon from '@mui/icons-material/Forum';

import { ProjectCommunication } from '../../model';
import ProjectCommunicationDetails from '../components/ProjectCommunicationDetails';
import ProjectCommunicationUpdateForm from '../components/ProjectCommunicationUpdateForm';
import axios from '../../../sharedModules/utils/axios.utils';

const ProjectCommunicationDetailPage: React.FunctionComponent = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const history = useHistory();

  const [communication, setCommunication] = useState<ProjectCommunication | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const fetchCommunication = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get<string, any>(`/customer_issues/servman_project_communications/${jobId}`);
      if (response?.data) {
        if (response.data.recno === '0' || response.data.recno === 0) {
          setError(
            'This project communication does not exist yet. The ability to start a new communication record is still under development.',
          );
          setCommunication(null);
        } else {
          setCommunication(response.data);
        }
      } else {
        setError('Project communication not found');
      }
    } catch (err: any) {
      console.error('Error fetching project communication:', err);
      setError('Failed to load project communication. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchCommunication();
    }
  }, [jobId]);

  const handleUpdateSuccess = () => {
    fetchCommunication();
  };

  const handleBackClick = () => {
    history.push('/project_communications/search');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={handleBackClick}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Project Communications
        </Link>
        <Typography color="text.primary">Job {communication?.job}</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ForumIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
              Communication Details
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Back to List
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {communication && !loading && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <ProjectCommunicationDetails communication={communication} showStatusChip={true} />

            <Divider sx={{ my: 3 }} />

            <ProjectCommunicationUpdateForm communication={communication} onSuccess={handleUpdateSuccess} />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ProjectCommunicationDetailPage;
